
import { useState, useEffect } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Shield, AlertTriangle } from "lucide-react";
import { screenCoinName } from "@/utils/nameScreening";

interface NameScreeningProps {
  name: string;
  onValidationChange: (isValid: boolean) => void;
}

export function NameScreening({ name, onValidationChange }: NameScreeningProps) {
  const { toast } = useToast();
  const [screeningResult, setScreeningResult] = useState<{
    isApproved: boolean;
    reasons: string[];
    sentimentScore: number;
  } | null>(null);

  useEffect(() => {
    if (name.length > 0) {
      const result = screenCoinName(name);
      setScreeningResult(result);
      onValidationChange(result.isApproved);
    } else {
      setScreeningResult(null);
      onValidationChange(false);
    }
  }, [name, onValidationChange]);

  const handleReport = () => {
    toast({
      title: "Report Submitted",
      description: "Thank you for helping keep our platform safe.",
    });
  };

  if (!screeningResult || name.length === 0) return null;

  return (
    <div className="space-y-4 mt-4">
      {!screeningResult.isApproved ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Community Name Check Failed</AlertTitle>
          <AlertDescription>
            <ul className="list-disc list-inside mt-2">
              {screeningResult.reasons.map((reason, index) => (
                <li key={index}>{reason}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      ) : (
        <Alert variant="default" className="border-green-500">
          <Shield className="h-4 w-4 text-green-500" />
          <AlertTitle>Community Name Approved</AlertTitle>
          <AlertDescription>
            This name passes our community guidelines and automated screening checks.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex items-center gap-2">
        <Badge variant={screeningResult.sentimentScore >= 0 ? "default" : "destructive"}>
          Sentiment: {screeningResult.sentimentScore.toFixed(2)}
        </Badge>
        <Button
          variant="outline"
          size="sm"
          onClick={handleReport}
          className="ml-auto"
        >
          Report Name
        </Button>
      </div>
    </div>
  );
}
